'use client'

import { sendGTMEvent } from '@next/third-parties/google'

// Track names should be in the format of "Category Action"
// Example: "Product Viewed", "Cart Updated", "Checkout Completed"
export type TrackEventName =
  // Product events
  | 'Product Viewed'
  | 'Product Added'
  | 'Product Removed'
  | 'Product Updated'
  | 'Product List Viewed'
  | 'Product List Filtered'

  // Cart events
  | 'Cart Viewed'
  | 'Cart Updated'
  | 'Cart Item Added'
  | 'Cart Item Removed'
  | 'Cart Item Updated'

  // Checkout events
  | 'Checkout Started'
  | 'Checkout Step Viewed'
  | 'Checkout Step Completed'
  | 'Payment Method Selected'
  | 'Order Completed'
  | 'Order Refunded'

  // B2B events
  | 'Store Created'
  | 'Store Updated'
  | 'Inventory Updated'
  | 'Price Updated'
  | 'AI Price Suggested'
  | 'AI Price Accepted'

  // User events
  | 'Page Viewed'
  | 'Section Viewed'
  | 'Text Selected'

  // Section-specific events
  | 'Hero Viewed'
  | 'Hero CTA Clicked'
  | 'Hero Media Interaction'
  | 'CTA Viewed'
  | 'CTA Button Clicked'
  | 'Pricing Viewed'
  | 'Pricing Plan Selected'
  | 'Features Viewed'
  | 'Feature Interaction'

  // Account events
  | 'Account Created'
  | 'Account Updated'
  | 'Signed In'
  | 'Signed Out'
  | 'Error Occurred'

  // Contact / demo events
  | 'Contact Form Opened'
  | 'Contact Form Closed'
  | 'Arrange a demo clicked'
  | 'Book Demo Directly Clicked'
  | 'Hero Demo Meeting Booked'
  | 'Contact Form Submitted'
  | 'App Download Clicked'
  | 'Download Gate Submitted'
  | 'Download Gate Link Clicked'
  | 'Pricing CTA Clicked'
  | 'Footer Contact Clicked'
  | 'Footer Event Button Clicked'
  | 'Sales Banner Clicked'
  | 'Sales Banner Dismissed'
  | 'FAQ Expanded'
  | 'Video Played'
  | 'Content Slider Slide Viewed'
  | 'Content Slider Navigation Clicked'

  // Calculator events
  | 'Calculator Viewed'
  | 'Calculator Tab Viewed'
  | 'Calculator Report Gate Opened'
  | 'Calculator Report Gate Submitted'
  | 'Calculator CTA Clicked'

  // Local shop window (Digital Window) events
  | 'Shop Directions Clicked'
  | 'Shop Phone Clicked'

  // Custom events
  | string

let isDebug = true
let enableTracking = true

const isBrowser = (): boolean => typeof window !== 'undefined' && typeof document !== 'undefined'

async function getEnv(keys: string[]): Promise<Record<string, boolean>> {
  if (!isBrowser()) {
    // During SSG/SSR, avoid relative fetch and return safe defaults
    return Object.fromEntries(keys.map((k) => [k, false])) as Record<string, boolean>
  }
  const response = await fetch('/api/config?key=' + keys.join(','))
  return response.json()
}

async function updateEnv() {
  const env = await getEnv(['NEXT_PUBLIC_DEBUG', 'NEXT_PUBLIC_ENABLE_TRACKING'])
  isDebug = env.NEXT_PUBLIC_DEBUG
  enableTracking = env.NEXT_PUBLIC_ENABLE_TRACKING
}

// Update debug flag every 60 seconds (browser only)
if (isBrowser()) {
  ;(async () => {
    await updateEnv()
    setInterval(updateEnv, 60000)
  })()
}

type TrackEventOptions = {
  gtm?: boolean
}

/**
 * Enhanced tracking function that integrates with Next.js built-in tracking
 * and supports multiple analytics providers
 */
export function trackEvent(
  name: TrackEventName,
  params?: Record<string, unknown>,
  { gtm = true }: TrackEventOptions = {},
) {
  // Skip tracking if globally disabled
  if (!enableTracking) {
    return
  }

  // Gather common data for enrichment
  const commonData = {
    timestamp: Date.now(),
    path: typeof window !== 'undefined' ? window.location.pathname : undefined,
    url: typeof window !== 'undefined' ? window.location.href : undefined,
    referrer: typeof document !== 'undefined' ? document.referrer : undefined,
    screen_width: typeof window !== 'undefined' ? window.innerWidth : undefined,
    screen_height: typeof window !== 'undefined' ? window.innerHeight : undefined,
    market: typeof window !== 'undefined' ? window.location.hostname.split('.')[0] : undefined,
    language: typeof navigator !== 'undefined' ? navigator.language : undefined,
    page_title: typeof document !== 'undefined' ? document.title : undefined,
  }

  // Enrich data with common parameters
  const enrichedParams = {
    ...commonData,
    ...params,
  }

  // Google Tag Manager - use Next.js built-in helper
  if (gtm) {
    sendGTMEvent({ event: name, ...enrichedParams })
  }

  // Log event in development
  if (process.env.NODE_ENV === 'development' || isDebug) {
    console.log(`[Tracking] ${name}`, enrichedParams)
  }
}

/**
 * Track a page view event
 */
export function trackPageView(
  pageName: string,
  pageProps?: Record<string, unknown>
) {
  // Skip tracking if globally disabled
  if (!enableTracking) {
    return
  }

  // Gather page metadata
  const metadata = {
    title: typeof document !== 'undefined' ? document.title : '',
    referrer: typeof document !== 'undefined' ? document.referrer : '',
    url: typeof window !== 'undefined' ? window.location.href : '',
    path: typeof window !== 'undefined' ? window.location.pathname : '',
    ...pageProps
  }

  trackEvent('Page Viewed', {
    page_name: pageName,
    ...metadata
  })
}